import { useContext } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { OrganisationTypeConstants } from "../../../../constants";
import { Origin, TabsList } from "../../../../models";
import { IStateOrigin } from "../../../../navigators/navigateStateObjects";
import { GetProjectDetailsResponse } from "../../../../service/query";
import { useAuth } from "../../../../useAuth";
import {
  getDashboardRoute,
  getProjectAccessRoute,
  getProjectActivitiesRoute,
  getProjectAuditHistoryRoute,
  getProjectDetailsByUuid,
  getProjectDetailsRoute,
  getProjectDocumentsRoute,
  getProjectInventoryRoute,
  getProjectLocationRoute,
  getProjectsRoute,
  getProjectTasksRoute,
  getSettingsAuditHistoryRoute,
  getWorkflowTemplatesRoute,
} from "../../../../utils/routes";
import { ProjectContext } from "../../../shared/projects/project/ProjectContext";

interface UseProjectReturnData {
  projectDetails: GetProjectDetailsResponse | undefined;
  handleBackClick: () => void;
  tabsList: TabsList;
  backButtonText: string;
}

export const useProject = (): UseProjectReturnData => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { currentUserType } = useAuth();
  const { projectDetails } = useContext(ProjectContext);
  const { state } = useLocation();
  const stateOrigin = state?.origin as IStateOrigin;
  const origin = searchParams.get("origin");

  let backButtonText = stateOrigin?.goBackText;

  if (!backButtonText) {
    switch (origin) {
      case Origin.ProjectAuditPage:
      case Origin.OrganisationAuditPage:
        backButtonText = "Back to audit";
        break;
      case Origin.WorkflowsPage:
        backButtonText = "Back to workflows";
        break;
      case Origin.DashboardPage:
        backButtonText = "Back to dashboard";
        break;
      default:
        backButtonText = "Back to projects";
        break;
    }
  }

  const handleBackClick = (): void => {
    if (stateOrigin?.from) {
      navigate(stateOrigin.from);
      return;
    }

    switch (origin) {
      case Origin.ProjectAuditPage:
        if (projectDetails?.uuid) {
          navigate(getProjectAuditHistoryRoute(projectDetails.uuid, currentUserType));
        }
        break;
      case Origin.OrganisationAuditPage:
        navigate(getSettingsAuditHistoryRoute(currentUserType));
        break;
      case Origin.WorkflowsPage:
        navigate(getWorkflowTemplatesRoute(currentUserType));
        break;
      case Origin.DashboardPage:
        navigate(getDashboardRoute(currentUserType));
        break;
      default:
        navigate(getProjectsRoute(currentUserType));
    }
  };

  const getTabsList = (): TabsList => {
    const list = [
      {
        label: "Overview",
        route: getProjectDetailsByUuid(
          projectDetails?.uuid || "",
          OrganisationTypeConstants.DEVELOPMENT_MANAGER,
          origin
            ? {
                origin,
              }
            : undefined
        ),
      },
      {
        label: "Details",
        route: getProjectDetailsRoute(
          projectDetails?.uuid || "",
          OrganisationTypeConstants.DEVELOPMENT_MANAGER,
          origin
            ? {
                origin,
              }
            : undefined
        ),
      },
      {
        label: "Location",
        route: getProjectLocationRoute(
          projectDetails?.uuid || "",
          OrganisationTypeConstants.DEVELOPMENT_MANAGER,
          origin
            ? {
                origin,
              }
            : undefined
        ),
      },
      {
        label: "Inventory",
        route: getProjectInventoryRoute(
          projectDetails?.uuid || "",
          OrganisationTypeConstants.DEVELOPMENT_MANAGER,
          origin
            ? {
                origin,
              }
            : undefined
        ),
      },
      {
        label: "Activities",
        route: getProjectActivitiesRoute(
          projectDetails?.uuid || "",
          OrganisationTypeConstants.DEVELOPMENT_MANAGER,
          origin
            ? {
                origin,
              }
            : undefined
        ),
      },
      {
        label: "Documents",
        route: getProjectDocumentsRoute(
          projectDetails?.uuid || "",
          OrganisationTypeConstants.DEVELOPMENT_MANAGER,
          origin
            ? {
                origin,
              }
            : undefined
        ),
      },
      {
        label: "Tasks",
        route: getProjectTasksRoute(
          projectDetails?.uuid || "",
          OrganisationTypeConstants.DEVELOPMENT_MANAGER,
          origin
            ? {
                origin,
              }
            : undefined
        ),
      },
      {
        label: "Access",
        route: getProjectAccessRoute(
          projectDetails?.uuid || "",
          OrganisationTypeConstants.DEVELOPMENT_MANAGER,
          origin
            ? {
                origin,
              }
            : undefined
        ),
      },
    ];

    return list;
  };

  return {
    projectDetails,
    handleBackClick,
    tabsList: getTabsList(),
    backButtonText,
  };
};
